import { DialogContent, DialogProps } from '@material-ui/core';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { FullScreenDialogWithStepper } from 'framework/dialogs/FullScreenDialogWithStepper';
import { FormDatePicker } from 'framework/forms/FormDatePicker';
import { FormDatePickerWithUnit } from 'framework/forms/FormDatePickerWithUnit';
import { FormSingleCheckboxField } from 'framework/forms/FormSingleCheckboxField';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormDialogActions } from 'framework/forms/PageableFormDialogActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { setFormValue } from 'framework/forms/utils/setFormValue';
import { setLineErrors } from 'framework/forms/utils/setLineErrors';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { IRegisterPurchaseRequest, purchasesCommand_new, purchasesQuery_nextReference, purchasesQuery_settings, ValidUntilTimeUnits } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo, useState } from 'react';
import { useNextReferenceInterceptor } from 'shared/interceptors/useNextReferenceInterceptor';
import { useNow } from 'shared/utils/useNow';
import * as yup from 'yup';
import { FormSelectOrCreateSupplierField } from '../../../../contacts/suppliers/forms/FormSelectOrCreateSupplierField';
import { SearchProvider } from '../context/SearchProvider';
import { IPurchaseFormLine } from './IPurchaseFormLine';
import { PurchaseFormConfirmation } from './PurchaseFormConfirmation';
import { SearchAndSelectProductsForPurchase } from './SearchAndSelectProductsForPurchase';
import { mapToRegisterPurchaseLineRequest } from './mapToRegisterPurchaseLineRequest';

const createSchema = (strings: IStrings) => {
	return yup
		.object<IRegisterPurchaseRequest>({
			validUntil: yup.date().required(),
			lines: yup.mixed(),
			reference: yup.string().required(),
			externalReference: yup.string(),
			isAutoSelectReference: yup.boolean().defined(),
			supplierId: yup.string().required(),
			purchaseDate: yup.date().required(),
		})
		.defined();
};

const EmptyValues: IRegisterPurchaseRequest = {
	lines: [],
	reference: '',
	isAutoSelectReference: true,
	validUntil: undefined as any,
	supplierId: '',
	purchaseDate: undefined as any,
	externalReference: '',
};

const stepsRecord: Record<number, (keyof IRegisterPurchaseRequest)[]> = {
	0: ['supplierId'],
	1: ['reference', 'isAutoSelectReference', 'externalReference', 'purchaseDate', 'validUntil'],
	2: ['lines'],
	3: [],
};

interface IProps extends DialogProps {
	confirm: (id: string) => void;
	cancel: VoidFunction;
}

export const RegisterPurchaseRequestForm = ({ confirm, cancel, ...rest }: IProps) => {
	const strings = useLocalization();
	const [lines, setLines] = useState<IPurchaseFormLine[]>([]);
	const [step, setStep] = useState<number>(0);
	const schema = useMemo(() => createSchema(strings), [strings]);
	const [create, isSubmitting] = useFormSubmit(purchasesCommand_new);
	const [isShiftVat, setIsShiftVat] = useState<boolean>(false);
	const notify = useSnackbarNotify();
	const now = useNow();

	const handleSubmit = async (values: IRegisterPurchaseRequest, helpers: FormikHelpers<IRegisterPurchaseRequest>) => {
		if (lines.length === 0) {
			notify(strings.selectAtLeastOneWhat(strings.line), 'error');
		} else {
			const r = await create({ ...values, lines: lines.map((t, index) => mapToRegisterPurchaseLineRequest(t, index, lines, isShiftVat)) });
			if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
				notify('Aankoop geregistreerd');
				confirm(r.result.objectId);
			} else {
				setLineErrors('lines', r, lines, setLines);
			}
		}
	};

	if (now === undefined) {
		return <div></div>;
	}

	return (
		<Formik<IRegisterPurchaseRequest>
			validateOnMount
			initialValues={{ ...EmptyValues, purchaseDate: now }}
			validationSchema={schema}
			onSubmit={handleSubmit}>
			<Form>
				<SearchProvider>
					<InnerDialog
						{...rest}
						isSubmitting={isSubmitting}
						cancel={cancel}
						lines={lines}
						setLines={setLines}
						step={step}
						setStep={setStep}
						stepsRecord={stepsRecord}
						schema={schema}
						isShiftVat={isShiftVat}
						setIsShiftVat={setIsShiftVat}
					/>
				</SearchProvider>
			</Form>
		</Formik>
	);
};

interface IInnerProps extends DialogProps {
	cancel: VoidFunction;
	lines: IPurchaseFormLine[];
	setLines: React.Dispatch<React.SetStateAction<IPurchaseFormLine[]>>;
	isSubmitting: boolean;
	step: number;
	setStep: React.Dispatch<React.SetStateAction<number>>;
	stepsRecord: Record<number, (keyof IRegisterPurchaseRequest)[]>;
	schema: yup.ObjectSchema<IRegisterPurchaseRequest>;
	setIsShiftVat: React.Dispatch<React.SetStateAction<boolean>>;
	isShiftVat: boolean;
}

export const InnerDialog = ({ cancel, lines, setLines, isSubmitting, step, setStep, stepsRecord, schema, isShiftVat, setIsShiftVat, ...rest }: IInnerProps) => {
	const props = useFormikContext<IRegisterPurchaseRequest>();
	const strings = useLocalization();
	useNextReferenceInterceptor(purchasesQuery_nextReference, props);
	const [settings] = useApiEffect(purchasesQuery_settings);

	const setLinesX = (lines: IPurchaseFormLine[]) => {
		// setFormValue<IRegisterPurchaseRequest>('lines', lines.map((t, index) => lineToModel(t, index, undefined)), props);
		setFormValue<IRegisterPurchaseRequest>('lines', [], props);
		setLines(lines);
	};

	if (settings === undefined) {
		return <div></div>;
	}

	return (
		<FullScreenDialogWithStepper
			{...rest}
			fullScreenStep={2}
			title={strings.newWhat(strings.purchase)}
			step={step}
			labels={[strings.supplier, strings.reference, strings.items, strings.confirmation]}>
			<DialogContent
				dividers
				className='df-col fg1'>
				{step === 0 && (
					<FormSelectOrCreateSupplierField<IRegisterPurchaseRequest>
						name='supplierId'
						label={strings.supplier}
					/>
				)}
				{step === 1 && (
					<>
						<FormSingleCheckboxField<IRegisterPurchaseRequest>
							name='isAutoSelectReference'
							label={strings.autoSelectIndexNumberQuestion}
						/>
						<FormTextField<IRegisterPurchaseRequest>
							name='reference'
							label={strings.invoiceNumber}
							disabled={props.values.isAutoSelectReference}
						/>
						<FormTextField<IRegisterPurchaseRequest>
							name='externalReference'
							label={strings.externalReference}
						/>
						<FormDatePicker<IRegisterPurchaseRequest>
							name='purchaseDate'
							label={strings.purchaseDate}
							required
						/>
						<FormDatePickerWithUnit<IRegisterPurchaseRequest>
							name='validUntil'
							label={strings.toPayBefore}
							units={ValidUntilTimeUnits}
							defaultUnit='Months'
							defaultValue={settings.defaultValidUntil}
						/>
					</>
				)}
				{step === 2 && (
					<SearchAndSelectProductsForPurchase
						lines={lines}
						setLines={setLinesX}
						supplierId={props.values.supplierId}
						isShiftVat={isShiftVat}
						setIsShiftVat={setIsShiftVat}
					/>
				)}
				{step === 3 && (
					<PurchaseFormConfirmation
						lines={lines}
						isShiftVat={isShiftVat}
					/>
				)}
			</DialogContent>
			<PageableFormDialogActions
				step={step}
				setStep={setStep}
				cancel={cancel}
				isSubmitting={isSubmitting}
				submitText={strings.create}
				schema={schema}
				stepsRecord={stepsRecord}
			/>
		</FullScreenDialogWithStepper>
	);
};
